import React, { useContext, useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router'
import PageHeader from '../../components/page-header'
import DetailsSections from '../../components/details-sections'
import { checkIcon, warningIcon } from '../../components/tracker/tracker'
import TaskProgress from '../../components/task-progress'
import TaskNotFound from '../../components/task-not-found'
import Alert from '../../components/alert'
import Button from '../../components/button'
import Dialog from '../../components/dialog'
import OtpInput from '../../components/otp-input'
import Textarea from '../../components/fields/textarea'
import { useTranslation, useTask } from '../../hooks'
import { AppContext } from '../../contexts'
import useDateTime from '../../hooks/useDateTime'
import { maskEmail } from '../../utils'
import './_taskReview.scss'

const documentIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.6667 1.6665H5.83333C5.39131 1.6665 4.96738 1.84209 4.65482 2.15465C4.34226 2.46721 4.16667 2.89114 4.16667 3.33317V16.6665C4.16667 17.1085 4.34226 17.5325 4.65482 17.845C4.96738 18.1576 5.39131 18.3332 5.83333 18.3332H14.1667C14.6087 18.3332 15.0326 18.1576 15.3452 17.845C15.6577 17.5325 15.8333 17.1085 15.8333 16.6665V5.83317L11.6667 1.6665Z" stroke="#666C73" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.6667 1.6665V5.83317H15.8333" stroke="#666C73" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const downloadIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.66667 6.66669L8 10L11.3333 6.66669" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 10V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const OTP_LENGTH = 6;
const OTP_RESEND_SECONDS = 60;
// Acknowledging or rejecting after this long on the page asks the customer to verify first
const VERIFY_AFTER_MS = 15 * 60 * 1000;

function TaskReview() {
  const { i18n } = useTranslation();
  const { formatDateTime } = useDateTime();
  const [isReviewed, setIsReviewed] = useState(false);
  // null while the report is being reviewed, then 'acknowledged' or 'rejected'
  const [decision, setDecision] = useState(null);
  const [decidedAt, setDecidedAt] = useState();
  // Which modal is open: 'acknowledge', 'reject' or 'otp'
  const [modal, setModal] = useState(null);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [reason, setReason] = useState('');
  const [otp, setOtp] = useState('');
  const [resendIn, setResendIn] = useState(OTP_RESEND_SECONDS);
  const openedAt = useRef(Date.now());
    const { taskId } = useParams();
  const { appointmentData = {} } = useContext(AppContext);
  const { task, details, steps: taskSteps, mapAllowed, origin, destination } = useTask(taskId);
  const { report } = task || {};
  const email = maskEmail(appointmentData.email);
  const isDecided = !!decision;
  const isRejected = decision === 'rejected';

  useEffect(() => {
    if (modal !== 'otp' || resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn(seconds => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [modal, resendIn]);

  const finalStep = isDecided
    ? {
      title: isRejected ? 'cp_report_rejected' : 'cp_report_acknowledged',
      subTitle: formatDateTime(decidedAt),
      isComplete: true,
      isRejected
    }
    : { title: 'cp_acknowledgement_needed', subTitle: 'cp_action_required', isActive: true };
  const steps = [...taskSteps, finalStep];

  const closeModal = () => {
    setModal(null);
    setPendingDecision(null);
    setOtp('');
  };

  const finalize = (value) => {
    // TODO: send the acknowledgement, or the rejection with its reason, to the backend once the API exists
    setDecidedAt(new Date().toISOString());
    setDecision(value);
    setModal(null);
    setPendingDecision(null);
    setOtp('');
  };

  // Customers who have been on the page a while verify with a code before the decision is recorded
  const handleDecision = (value) => {
    if (Date.now() - openedAt.current <= VERIFY_AFTER_MS) return finalize(value);
    setPendingDecision(value);
    setResendIn(OTP_RESEND_SECONDS);
    setModal('otp');
  };

  // This dialog is only the UI for verification. The backend must verify the code and the decision itself,
  // because nothing on this page is a security check.
  const handleVerify = () => {
    // TODO: validate the code with the backend once the API exists
    finalize(pendingDecision);
  };

  const formatCountdown = (seconds) => `0:${String(seconds).padStart(2, '0')}`;

  if (!task) return <TaskNotFound />

  const reportFile = report && (
    <div className="task-review__file">
      <span className="task-review__file-icon">{documentIcon}</span>
      <span className="task-review__file-labels">
        <span className="task-review__file-name">{report.fileName}</span>
        {report.fileSize && <span className="task-review__file-size">{report.fileSize}</span>}
      </span>
      <Button theme="outline" href={report.url} disabled={!report.url} testId="download-report">
        {downloadIcon}
        {i18n('cp_download')}
      </Button>
    </div>
  );

  return (
    <div className="container task-review">
      <PageHeader title={i18n('cp_work_order')} to="/order_details" />
      {isDecided ? (
        <div className={`task-review__result task-review__result--${decision}`} data-testid="review-result">
          <span className="task-review__result-icon">{isRejected ? warningIcon : checkIcon}</span>
          <h2 className="task-review__result-title">{i18n(isRejected ? 'cp_report_rejected' : 'cp_report_acknowledged')}</h2>
          <p className="task-review__result-copy">{i18n(isRejected ? 'cp_report_rejected_copy' : 'cp_report_acknowledged_copy')}</p>
          {!!email && (
            <p className="task-review__result-copy">
              {i18n(isRejected ? 'cp_response_emailed_to' : 'cp_copy_emailed_to')} {email}
            </p>
          )}
        </div>
      ) : (
        <Alert type="info" label={i18n('cp_report_ready_for_review')} />
      )}
      <div className="card task-review__card">
        <DetailsSections title={i18n('cp_appointment_details')} items={details} />
      </div>
      <div className="card task-review__card">
        {isDecided ? (
          <>
            <h2 className="task-review__section-title">{i18n(isRejected ? 'cp_report_rejected' : 'cp_report')}</h2>
            {isRejected && (
              <div className="task-review__reason">
                <span className="task-review__reason-label">{i18n('cp_what_was_the_problem')}</span>
                <span>{reason}</span>
              </div>
            )}
            {reportFile}
          </>
        ) : (
          <>
            <h2 className="task-review__section-title">{i18n('cp_report_review')}</h2>
            {reportFile}
            <label className="task-review__confirm">
              <input
                type="checkbox"
                checked={isReviewed}
                onChange={e => setIsReviewed(e.target.checked)}
                data-testid="report-reviewed-checkbox"
              />
              {i18n('cp_report_reviewed_confirm')}
            </label>
            <div className="task-review__actions">
              <Button disabled={!isReviewed} onClick={() => setModal('acknowledge')} testId="acknowledge-report">
                {i18n('cp_acknowledge_report')}
              </Button>
              <Button theme="outline" disabled={!isReviewed} onClick={() => setModal('reject')} testId="reject-report">
                {i18n('cp_reject_report')}
              </Button>
            </div>
          </>
        )}
      </div>
      <TaskProgress steps={steps} mapAllowed={mapAllowed} origin={origin} destination={destination} />
      <Dialog
        title={i18n('cp_acknowledge_report_title')}
        isOpen={modal === 'acknowledge'}
        onClose={closeModal}
        testId="acknowledge-dialog"
        actions={(
          <>
            <Button theme="outline" onClick={closeModal} testId="acknowledge-cancel">{i18n('cp_cancel')}</Button>
            <Button onClick={() => handleDecision('acknowledged')} testId="acknowledge-confirm">{i18n('cp_acknowledge')}</Button>
          </>
        )}
      >
        <p>{i18n('cp_acknowledge_report_copy')}</p>
      </Dialog>
      <Dialog
        title={i18n('cp_reject_report_title')}
        isOpen={modal === 'reject'}
        onClose={closeModal}
        testId="reject-dialog"
        actions={(
          <>
            <Button theme="outline" onClick={closeModal} testId="reject-cancel">{i18n('cp_cancel')}</Button>
            <Button theme="danger" disabled={!reason.trim()} onClick={() => handleDecision('rejected')} testId="reject-confirm">
              {i18n('cp_reject_report')}
            </Button>
          </>
        )}
      >
        <p>{i18n('cp_reject_report_copy')}</p>
        <div className="task-review__reason-field">
          <Textarea
            name="reason"
            label={`* ${i18n('cp_whats_the_problem')}`}
            placeholder={i18n('cp_reject_reason_placeholder')}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </div>
      </Dialog>
      <Dialog
        title={i18n('cp_verify_its_you')}
        isOpen={modal === 'otp'}
        onClose={closeModal}
        testId="otp-dialog"
        actions={(
          <Button disabled={otp.length < OTP_LENGTH} onClick={handleVerify} testId="otp-confirm">{i18n('cp_confirm')}</Button>
        )}
      >
        <p>{email ? i18n('cp_verify_copy', { email }) : i18n('cp_verify_copy_no_email')}</p>
        <div className="task-review__otp">
          <span className="task-review__otp-label">{i18n('cp_verification_code')}</span>
          <OtpInput length={OTP_LENGTH} value={otp} onChange={setOtp} label={i18n('cp_verification_code')} />
          {resendIn > 0 ? (
            <span className="task-review__otp-resend">{i18n('cp_resend_code')}: {formatCountdown(resendIn)}</span>
          ) : (
            // TODO: request a new code from the backend once the API exists
            <button type="button" className="task-review__otp-resend task-review__otp-resend--action" onClick={() => setResendIn(OTP_RESEND_SECONDS)} data-testid="otp-resend">
              {i18n('cp_resend_code')}
            </button>
          )}
        </div>
      </Dialog>
    </div>
  )
}

export default TaskReview
